import { Dispatch, SetStateAction, useContext, useState } from "react";
import { Modal, Button, Switchbox } from "@weirgroup/weir-dls";
import { useTranslation } from "react-i18next";

import { AppConfigContext } from "../../../contexts/app-config.context";
import { FetchedUserSettingType, UserSettingType } from "../../../types/user.type";
import userSettingsApi from "../../../api/settings.api";
import { processResult } from "../../../utils/process-result.util";
import { SetFunctionType } from "../../../types/utils.type";

import "../styles.scss";
import { DateRangePicker, DateRangePickerProps } from "rsuite";
import { useGlobalError } from "../../../contexts/global-error.context";
import './styles.scss'
import { useAnalyticsEvent } from "../../../hooks/useAnalyticsEvent";
import { LoadingSpinner } from "../../../components/loading-spinner";
interface Props {
    isSettingsModalShown: boolean
    setIsSettingsModalShown: Dispatch<SetStateAction<boolean>>;
    startDate: Date;
    endDate: Date;
    setStartDate: Dispatch<SetStateAction<Date>>;
    setEndDate: Dispatch<SetStateAction<Date>>;
    fetchNotifications: () => void;
    setToastData: Dispatch<SetStateAction<any>>;
    setShowToast: Dispatch<SetStateAction<any>>;
    userSettings: FetchedUserSettingType;
    fetchNotificationConfigs: () => void;
    darkMode?:boolean;
}

export const SettingsModal = (props: Props) => {
    const {
        isSettingsModalShown,
        setIsSettingsModalShown,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        fetchNotifications,
        setToastData,
        setShowToast,
        userSettings,
        fetchNotificationConfigs,
        darkMode
    } = props;
    const logAnalyticsEvent = useAnalyticsEvent();
    const appConfigStore = useContext(AppConfigContext);
    const { showError } = useGlobalError();
    const [geofenceEmailNotification, setGeofenceEmailNotification] = useState<boolean>(userSettings.geofence_email_notification);
    const [assetEmailNotification, setAssetEmailNotification] = useState<boolean>(userSettings.asset_email_notification);
    const [inAppNotification, setInAppNotification] = useState<boolean>(userSettings.inapp_notification);

    // Handlers for each switch
    const handleAssetEmailNotificationChange = (e: any) => {
        setAssetEmailNotification(!assetEmailNotification);
        logAnalyticsEvent('user_toggled_notification_setting', { description: 'User toggled notification setting', type: 'email_asset' });
    };

    const handleGeofenceEmailNotificationChange = (e: any) => {
        setGeofenceEmailNotification(!geofenceEmailNotification);
        logAnalyticsEvent('user_toggled_notification_setting', { description: 'User toggled notification setting', type: 'email_geofence' });
    };

    const handleInAppNotificationChange = (e: any) => {
        setInAppNotification(!inAppNotification);
        logAnalyticsEvent('user_toggled_notification_setting', { description: 'User toggled notification setting', type: 'inapp' });
    };
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const setFunctions: SetFunctionType = {
        setIsLoading,
        setIsModalShown: setIsSettingsModalShown,
        setToastData,
        setShowToast
    }
    const { t } = useTranslation();

    const saveConfigs = () => {
        setIsLoading(true)
        const userSettings: UserSettingType = {
            language: appConfigStore.language,
            timezone: appConfigStore.timezone,
            units: appConfigStore.units,
            asset_email_notification: assetEmailNotification,
            geofence_email_notification: geofenceEmailNotification,
            inapp_notification: inAppNotification
        }
        userSettingsApi.updateUserSettings(userSettings).then(() => {
            processResult(setFunctions, t('saveNotificationConfigMessage'), true)
            fetchNotifications();
            fetchNotificationConfigs();
        }).catch((err: any) => {
            const message = err.response.data.message ?? t('failNotificationConfigMessage')
            processResult(setFunctions, message, false)
            fetchNotifications();
            fetchNotificationConfigs();
        })
    }

    const onDateRangeChange:DateRangePickerProps["onChange"] = (dates) => {
        if(dates?.[0] && dates?.[1]){
            setStartDate(dates[0]);
            setEndDate(dates[1]);
            logAnalyticsEvent('user_updated_date_range', { description: 'User updated the notification date range' });
        } else {
            showError("Invalid date range")
        }
    }
    return (
        <div className="settings-config">
            <Modal
                darkMode={darkMode}
                showModal={isSettingsModalShown}
                onClose={() => setIsSettingsModalShown(false)}
                size="large"
                canvasDeviceSize="tablet"
                dismissable={true}
            >
                <div className="content">
                    <p className="title" style={{color:darkMode ? "white": "black"}}>{t('notificationSettings')}</p>
                    <div className="toggle-container">
                        <p className="tooltip" style={{color:darkMode ? "white": "gray"}}>{t("toggleTooltip")}</p>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={handleAssetEmailNotificationChange}
                                checked={assetEmailNotification}
                                label={t("assetEmailNotifications")}
                            />
                        </div>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={handleGeofenceEmailNotificationChange}
                                checked={geofenceEmailNotification}
                                label={t("geofenceEmailNotifications")}
                            />
                        </div>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={handleInAppNotificationChange}
                                checked={inAppNotification}
                                label={t("inAppNotifications")}
                            />
                        </div>
                    </div>
                    <div className="date-range">
                        <p className="tooltip" style={{color:darkMode ? "white": "gray"}}>{t("dateRangeToolTip")}</p>
                        <DateRangePicker
                            format='yyyy/MM/dd'
                            menuClassName="dateRangePicker"
                            character=' - '
                            cleanable={false}
                            onChange={onDateRangeChange}
                            defaultValue={[startDate, endDate]}

                        />
                    </div>
                </div>
                <div className="save-btn">
                    <Button
                        darkMode={darkMode}
                        label={t("saveSettings")}
                        size={"sm"}
                        isLoading={isLoading}
                        onClick={() => saveConfigs()}
                    />
                </div>
                {isLoading ? <LoadingSpinner darkMode={darkMode} />  : <></>}
            </Modal>
        </div>
    )
}